import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import BaseButton from "../../components/base/button/BaseButton.tsx";
import PageWrapper from "../../components/PageWrapper.tsx";
import { ErrorBoundary } from "react-error-boundary";
import { Suspense, useMemo } from "react";
import { ErrorHandler } from "@/components/error-hanlder/ErrorHandler.tsx";
import { logError } from "@/components/error-hanlder/error-handler.lib.ts";
import { useSuspenseQuery } from "@tanstack/react-query";
import {
  useCollectionAwardMutation,
  useTaskCollectionDetailOptions,
} from "./tasks.api.ts";
import { useLessonListOptions } from "@/pages/testing/testing.api";
import { useNavigate, useParams } from "react-router";
import ArrowUpRight from "@/assets/icons/arrow-up-right.svg?react";
import { formatNumber } from "@/shared/utils/index.ts";
import { TaskStatus } from "./tasks.types.ts";
import TaskLoading from "./components/cards/GetDonCardLoading.tsx";
import GetDoneSuccessIcon from "@/assets/icons/getdon_success.svg?react";
import { boom } from "@/lib/exposion.ts";
import showCustomToast from "@/shared/utils/showCustomToast.ts";
import { extractDomain, getImageByPriority } from "@/shared/utils/common.ts";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { openLink } from "@telegram-apps/sdk-react";
import GreatResult from "./GreatResult.tsx";
import IconPlay from "@/assets/icons/play.svg?react";
import IconUsers from "@/assets/icons/users.svg?react";
import IconNotes from "@/assets/icons/notes.svg?react";
import IconChevronRight from "@/assets/icons/chevron-right.svg?react";

const GetDonSingleLoading = () => {
  return (
    <div className="grid gap-3 overflow-hidden">
      <div className="relative skeleton w-full h-[203px] rounded-md shrink-0"></div>

      <div className="container">
        <div className="skeleton w-1/2 h-[22px] rounded-sm mb-1" />
        <div className="skeleton w-1/2 h-4 rounded-sm" />
      </div>
      <div className="container grid gap-[1px]">
        {Array.from({ length: 7 }).map((_, idx) => (
          <TaskLoading key={idx} />
        ))}
      </div>
    </div>
  );
};

const GetDonSingle = () => {
  return (
    <ErrorBoundary FallbackComponent={ErrorHandler} onError={logError}>
      <Suspense fallback={<GetDonSingleLoading />}>
        <BaseGetDonSingle />
      </Suspense>
    </ErrorBoundary>
  );
};

const BaseGetDonSingle = () => {
  const { t } = useTranslation();
  const { taskCollectionId } = useParams<{ taskCollectionId?: string }>();
  const navigate = useNavigate();

  const { data: taskDetail } = useSuspenseQuery(
    useTaskCollectionDetailOptions(taskCollectionId)
  );

  const { mutateAsync: getCollectionAward } = useCollectionAwardMutation();

  // Lesson list for education DON total
  const { data: lessons = [] } = useSuspenseQuery(useLessonListOptions(taskCollectionId));

  // Split tasks by admin-assigned section (Django Task.section field)
  const regularTasks = useMemo(
    () => taskDetail.tasks.filter((t) => t.section !== "SOCIAL"),
    [taskDetail]
  );
  const socialTasks = useMemo(
    () => taskDetail.tasks.filter((t) => t.section === "SOCIAL"),
    [taskDetail]
  );

  // DON totals per section
  const tasksDonTotal = useMemo(() => {
    return regularTasks.reduce((sum, task) => sum + Number(task.award || 0), 0);
  }, [regularTasks]);

  const socialDonTotal = useMemo(() => {
    return socialTasks.reduce((sum, task) => sum + Number(task.award || 0), 0);
  }, [socialTasks]);

  const educationDonTotal = useMemo(() => {
    // Keyword no longer awards DON — only the lesson test reward counts
    // toward the section total. (Backend mirror: keyword is just a gate.)
    return lessons.reduce((sum, lesson) => {
      return sum + Number(lesson.max_test_don || 0);
    }, 0);
  }, [lessons]);

  // All 3 sections are always visible and unlocked — users may complete any
  // section in any order. Empty sections route to a "no tasks yet" page.

  const taskAwards = useMemo(() => {
    const tasksSum = taskDetail.tasks.reduce((sum, task) => {
      if (task.user_process?.status === TaskStatus.Completed) {
        return sum + Number(task.award || 0);
      }
      return sum;
    }, 0);
    return tasksSum + Number(taskDetail.award || 0);
  }, [taskDetail]);

  const isPermittedToWithdrawDon = useMemo(() => {
    return taskDetail?.tasks.every(
      (task) => task.user_process?.status === TaskStatus.Completed
    );
  }, [taskDetail]);

  const handleGetDonAward = async () => {
    if (!isPermittedToWithdrawDon) {
      showCustomToast(t("not_all_tasks_completed"), "error");
      return;
    }
    if (!taskCollectionId) {
      showCustomToast(t("global_error"), "error");
      return;
    }

    await getCollectionAward(taskCollectionId);
    showCustomToast(t("congratulations"), "success", {
      description: t("get_done_successfully", {
        don: formatNumber(taskDetail.award),
      }),
      icon: <GetDoneSuccessIcon className="size-7" />,
    });
    boom();
    navigate("/get-don");
  };

  return (
    <PageWrapper backPath="/get-don">
      <div className="relative mb-3">
        <LazyLoadImage
          className="w-full aspect-[375/180] object-cover max-w-[calc(100%+2rem)]"
          effect="blur"
          wrapperProps={{
            style: {
              transition: "all 0.1s ease-in-out",
            },
          }}
          src={
            taskDetail.image
              ? getImageByPriority(taskDetail.image, "large")
              : "https://picsum.photos/375/180"
          }
          alt="Task collection banner"
        />
        {taskDetail.link && (
          <span
            onClick={() => {
              openLink(taskDetail.link);
            }}
            className="absolute right-3 bottom-3 cursor-pointer z-10 px-2.5 py-1.5 rounded-full font-medium bg-dark/60 text-white text-xs inline-flex items-center gap-1"
          >
            {extractDomain(taskDetail.link)}{" "}
            <ArrowUpRight className="size-[14px]" strokeWidth={2} />
          </span>
        )}
      </div>
      <div className="pb-28 flex flex-col gap-3 container overflow-x-hidden">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <h1 className="text-base font-semibold text-white leading-[22px]">
              {taskDetail.name}
            </h1>
          </div>
          <p className="text-xs font-medium leading-130 text-white">
            {taskDetail.description}
          </p>
        </div>

        {/* Section cards — hidden when section has no items, always unlocked
            when shown (any order, no sequential gate). */}
        <div className="flex flex-col">
          {[
            { key: "education", visible: taskDetail.has_education && lessons.length > 0, icon: IconPlay, label: t("education"), don: formatNumber(educationDonTotal), path: `/testing?collection=${taskCollectionId}` },
            { key: "tasks", visible: regularTasks.length > 0, icon: IconNotes, label: t("tasks"), don: formatNumber(tasksDonTotal), path: `/get-don/${taskCollectionId}/tasks` },
            { key: "social", visible: socialTasks.length > 0, icon: IconUsers, label: t("social_tab"), don: formatNumber(socialDonTotal), path: `/get-don/${taskCollectionId}/social` },
          ].filter((s) => s.visible).map((s) => {
            const Icon = s.icon;
            return (
              <div
                key={s.key}
                className="py-3 flex items-center gap-2 relative cursor-pointer"
                onClick={() => navigate(s.path)}
              >
                <div className="bg-white/08 rounded-full w-9 h-9 flex-center shrink-0">
                  <Icon className="size-5 text-white" />
                </div>
                <div className="w-full flex flex-col gap-0.5">
                  <p className="text-sm font-semibold leading-130 text-white">{s.label}</p>
                  <p className="text-xs leading-130 text-orange-100">
                    {s.don} DON
                  </p>
                </div>
                <IconChevronRight className="size-5 text-gray-1 shrink-0" />
                <hr className="absolute bottom-0 left-[45px] translate-y-[0.5px] border-none h-px w-full bg-white/08" />
              </div>
            );
          })}
        </div>
      </div>

      {createPortal(
        <div
          className="fixed bottom-safe-bottom container right-0 left-0 bg-[linear-gradient(180deg,#0E0E1400_0%,_#0E0E14_80%)] z-10"
          style={{
            willChange: "transform",
            transform: "translate3d(0, 0, 0)",
          }}
        >
          {taskDetail.is_user_awarded ? (
            <GreatResult taskAwards={taskAwards} />
          ) : (
            <BaseButton
              text={t("get_amount_don", {
                amount: formatNumber(taskDetail.award ?? 0),
              })}
              className="w-full font-normal tracking-wide text-xs text-left whitespace-normal break-words leading-tight py-3 "
              onClick={handleGetDonAward}
              disabled={!isPermittedToWithdrawDon}
            />
          )}
        </div>,
        document.body
      )}
    </PageWrapper>
  );
};

export default GetDonSingle;
